import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MuxPlayer from '@mux/mux-player-react';

import { isImageContentType, isMarkdownAttachment as isMarkdownAttachmentType, safeDimension } from '../../lib/attachments';
import { buildAttachmentLightboxSlide, isImageAttachmentForLightbox, isPdfAttachmentForLightbox } from '../../lib/lightboxSlides';
import { MarkdownAttachmentSourceTypes, markdownAttachmentSourceForAttachment } from '../../lib/markdownAttachment';
import { MediaAttachmentStates, getLocalThumbnail } from '../../lib/media_attachments_store';

import FilePlaceholderIcon from '../../../assets/images/file-placeholder.svg';

import { ImageFrame } from '../ImageFrame';
import MediaAttachmentUploadPreview from '../MediaAttachmentUploadPreview';
import { Tooltip } from '../shared/Tooltip';
import ImageAttachmentLightbox from './ImageAttachmentLightbox';
import MarkdownAttachment from './MarkdownAttachment';
import PdfAttachment from './PdfAttachment';
import UnreadMarker from './UnreadMarker';
import VideoAttachment from './VideoAttachment';

const IMAGE_CROSSFADE_DURATION_MS = 200;

export const Attachment = ({
  attachment,
  mode = 'single',
  isTopRight = false,
  shouldShowUnreadMarker = false,
  channelId,
  messageId,
  messageSortOrder,
  onUnreadMarkerDismiss,
  unreadMarkerWorkspaceId,
  unreadMarkerProfileId,
  onOpenImageLightbox,
  imageGallerySlides,
  imageGalleryIndex,
}) => {
  const [isImageLightboxOpen, setIsImageLightboxOpen] = useState(false);
  const [imageLightboxThumbBounds, setImageLightboxThumbBounds] = useState(null);
  const [localImagePreviewUrl, setLocalImagePreviewUrl] = useState(null);
  const [failedResolvedImageSrc, setFailedResolvedImageSrc] = useState(null);
  const localImagePreviewUrlRef = useRef(null);
  const localImagePreviewReleaseTimeoutRef = useRef(null);
  const uploadState = attachment?.upload?.state || null;
  const shouldRenderUploadPreview = Boolean(uploadState) && uploadState !== MediaAttachmentStates.completed;
  const isLocalImageUpload = Boolean(uploadState) && isImageContentType(attachment?.metadata?.content_type);
  const isImageAttachment = isImageAttachmentForLightbox(attachment);
  const isMarkdownAttachment = isMarkdownAttachmentType(attachment);
  const isPdfAttachment = isPdfAttachmentForLightbox(attachment);
  const isLightboxAttachment = isImageAttachment || isPdfAttachment;
  const isScrollerMode = mode === 'scroller';
  const mediumWidth = safeDimension(attachment?.variants?.medium?.width, null);
  const mediumHeight = safeDimension(attachment?.variants?.medium?.height, null);
  const metadataWidth = safeDimension(attachment?.metadata?.width, null);
  const metadataHeight = safeDimension(attachment?.metadata?.height, null);
  const lightboxBaseSlide = useMemo(() => buildAttachmentLightboxSlide(attachment), [attachment]);
  const markdownSource = useMemo(() => {
    if (!isMarkdownAttachment) return null;

    return markdownAttachmentSourceForAttachment({
      id: attachment?.id,
      public_url: attachment?.public_url,
      upload: uploadState ? { state: uploadState } : null,
    });
  }, [attachment?.id, attachment?.public_url, isMarkdownAttachment, uploadState]);
  const isLocalMarkdownAttachment = markdownSource?.type === MarkdownAttachmentSourceTypes.localUpload;
  // Preserve unknowns here so ImageFrame can derive ratio from thumbsize before using generic fallbacks.
  const imageWidth = mediumWidth || metadataWidth || null;
  const imageHeight = mediumHeight || metadataHeight || null;
  const isRetrying = uploadState === MediaAttachmentStates.retrying;
  const hasUploadError = uploadState === MediaAttachmentStates.error;
  const resolvedImageSrc = attachment?.variants?.medium?.public_url || null;
  const imageSrc = resolvedImageSrc || localImagePreviewUrl;
  const hasResolvedImageError = Boolean(resolvedImageSrc) && failedResolvedImageSrc === resolvedImageSrc;
  const hasGallery = Array.isArray(imageGallerySlides);
  const hasResolvedGallerySlide = hasGallery && Number.isInteger(imageGalleryIndex);
  const resolvedLightboxSlides = useMemo(
    () => (hasResolvedGallerySlide ? imageGallerySlides : lightboxBaseSlide ? [lightboxBaseSlide] : []),
    [hasResolvedGallerySlide, imageGallerySlides, lightboxBaseSlide],
  );
  const resolvedLightboxIndex = hasResolvedGallerySlide ? imageGalleryIndex : lightboxBaseSlide ? 0 : null;
  const activeLightboxSlide =
    isLightboxAttachment && Number.isInteger(resolvedLightboxIndex) ? resolvedLightboxSlides[resolvedLightboxIndex] || null : null;
  const canOpenImageLightbox = Boolean(activeLightboxSlide);
  const imageFrameClassName = canOpenImageLightbox ? 'cursor-pointer overflow-hidden rounded-2xl' : 'overflow-hidden rounded-2xl';
  // Send enough identity info upward so a root-level lightbox can close itself
  // if this message disappears (delete/replace/channel switch) while open.
  const imageLightboxPayload = useMemo(
    () =>
      canOpenImageLightbox
        ? {
            channelId,
            messageId: messageId || null,
            messageSortOrder: messageSortOrder || null,
            attachmentId: attachment?.id || null,
            src: activeLightboxSlide?.src || null,
            publicUrl: activeLightboxSlide?.publicUrl || null,
            previewSrc: activeLightboxSlide?.previewSrc || null,
            srcset: activeLightboxSlide?.srcset || null,
            alt: activeLightboxSlide?.alt || 'Attachment',
            width: activeLightboxSlide?.width,
            height: activeLightboxSlide?.height,
            slides: resolvedLightboxSlides,
            index: resolvedLightboxIndex,
          }
        : null,
    [
      activeLightboxSlide,
      attachment?.id,
      canOpenImageLightbox,
      channelId,
      messageId,
      messageSortOrder,
      resolvedLightboxSlides,
      resolvedLightboxIndex,
    ],
  );
  const hasExternalImageLightbox = typeof onOpenImageLightbox === 'function';

  useEffect(() => {
    let cancelled = false;

    if (!isLocalImageUpload || !attachment?.id || localImagePreviewUrlRef.current) return undefined;

    getLocalThumbnail(attachment.id).then((thumbnailBlob) => {
      if (cancelled || !thumbnailBlob) return;
      const objectUrl = URL.createObjectURL(thumbnailBlob);
      localImagePreviewUrlRef.current = objectUrl;
      setLocalImagePreviewUrl(objectUrl);
    });

    return () => {
      cancelled = true;
    };
  }, [attachment?.id, isLocalImageUpload]);

  const releaseLocalImagePreview = useCallback(() => {
    if (localImagePreviewReleaseTimeoutRef.current) {
      clearTimeout(localImagePreviewReleaseTimeoutRef.current);
      localImagePreviewReleaseTimeoutRef.current = null;
    }

    const objectUrl = localImagePreviewUrlRef.current;
    if (!objectUrl) return;

    localImagePreviewUrlRef.current = null;
    setLocalImagePreviewUrl(null);
    URL.revokeObjectURL(objectUrl);
  }, []);

  const handleResolvedImageLoad = useCallback(() => {
    setFailedResolvedImageSrc(null);
    if (!resolvedImageSrc || !localImagePreviewUrlRef.current) return;

    if (localImagePreviewReleaseTimeoutRef.current) {
      clearTimeout(localImagePreviewReleaseTimeoutRef.current);
    }
    localImagePreviewReleaseTimeoutRef.current = setTimeout(releaseLocalImagePreview, IMAGE_CROSSFADE_DURATION_MS);
  }, [releaseLocalImagePreview, resolvedImageSrc]);

  const handleResolvedImageError = useCallback(() => {
    if (!resolvedImageSrc) return;

    if (localImagePreviewReleaseTimeoutRef.current) {
      clearTimeout(localImagePreviewReleaseTimeoutRef.current);
      localImagePreviewReleaseTimeoutRef.current = null;
    }
    setFailedResolvedImageSrc(resolvedImageSrc);
  }, [resolvedImageSrc]);

  // The attachment component survives optimistic reconciliation, so its preview should too.
  useEffect(
    () => () => {
      if (localImagePreviewReleaseTimeoutRef.current) clearTimeout(localImagePreviewReleaseTimeoutRef.current);
      if (localImagePreviewUrlRef.current) URL.revokeObjectURL(localImagePreviewUrlRef.current);
    },
    [],
  );

  useEffect(
    () => () => {
      if (!localImagePreviewReleaseTimeoutRef.current) return;

      clearTimeout(localImagePreviewReleaseTimeoutRef.current);
      localImagePreviewReleaseTimeoutRef.current = null;
    },
    [resolvedImageSrc],
  );

  const handleOpenImageLightbox = useCallback(
    (event) => {
      if (!imageLightboxPayload) return;

      const openerElement = event?.currentTarget instanceof Element ? event.currentTarget : null;
      const openerRect = openerElement?.getBoundingClientRect?.();
      const thumbBounds =
        openerRect && Number.isFinite(openerRect.left) && Number.isFinite(openerRect.top) && Number.isFinite(openerRect.width)
          ? {
              x: openerRect.left,
              y: openerRect.top,
              w: openerRect.width,
            }
          : null;
      const nextPayload = {
        ...imageLightboxPayload,
        thumbBounds,
        slides: Array.isArray(imageLightboxPayload.slides)
          ? imageLightboxPayload.slides.map((slide, slideIndex) =>
              slideIndex === resolvedLightboxIndex ? { ...slide, thumbBounds } : slide,
            )
          : imageLightboxPayload.slides,
      };

      if (hasExternalImageLightbox) {
        // In virtualized lists we hoist the lightbox state to MessageFeed so
        // unmounting this row doesn't kill the overlay.
        onOpenImageLightbox(nextPayload);
        return;
      }

      // Standalone fallback (storybook/modals/etc.).
      setImageLightboxThumbBounds(thumbBounds);
      setIsImageLightboxOpen(true);
    },
    [hasExternalImageLightbox, imageLightboxPayload, onOpenImageLightbox, resolvedLightboxIndex],
  );
  const handleCloseImageLightbox = useCallback(() => {
    setImageLightboxThumbBounds(null);
    setIsImageLightboxOpen(false);
  }, []);

  const imageImgProps = useMemo(
    () => ({
      onClick: canOpenImageLightbox ? handleOpenImageLightbox : undefined,
      onLoad: handleResolvedImageLoad,
      onError: handleResolvedImageError,
      'data-media-attachment-id': attachment?.id,
    }),
    [attachment?.id, canOpenImageLightbox, handleOpenImageLightbox, handleResolvedImageError, handleResolvedImageLoad],
  );

  const renderMetadata = () => (
    <Tooltip label="Download original" className="min-w-0">
      <a
        href={attachment.public_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block truncate overflow-hidden font-mono text-3xs leading-none whitespace-nowrap no-underline hover:underline"
      >
        {attachment.metadata.file_name}
      </a>
    </Tooltip>
  );

  return (
    <div className={`relative flex w-fit flex-col gap-1 ${isScrollerMode ? 'shrink-0' : ''}`}>
      {isTopRight && shouldShowUnreadMarker && (
        <UnreadMarker
          workspaceId={unreadMarkerWorkspaceId}
          channelId={channelId}
          profileId={unreadMarkerProfileId}
          onDismiss={onUnreadMarkerDismiss}
        />
      )}
      {/* The old floating reaction chip lived here; image messages now use the
          standard hover cluster rendered by Message.jsx beside the bubble. */}
      {(isLocalImageUpload || isImageAttachment) && (
        <div className="relative w-fit">
          <ImageFrame
            src={imageSrc}
            alt={attachment?.metadata?.file_name || 'Image'}
            width={imageWidth}
            height={imageHeight}
            mode={mode}
            className={`${imageFrameClassName} ${isRetrying ? 'outline outline-yellow-400' : ''}`}
            imgProps={resolvedImageSrc ? imageImgProps : undefined}
            thumbhash={attachment?.metadata?.thumbhash}
            thumbsize={attachment?.metadata?.thumbsize}
            previewSrc={resolvedImageSrc ? localImagePreviewUrl : null}
          >
            {isImageAttachment ? renderMetadata() : null}
          </ImageFrame>

          {(hasUploadError || hasResolvedImageError) && (
            <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/50 px-1 text-center text-2xs leading-tight text-white">
              {attachment?.error || 'Image failed to load'}
            </div>
          )}
        </div>
      )}

      {/* Non-image uploads use the compact square placeholder until done. */}
      {shouldRenderUploadPreview && !isMarkdownAttachment && !isLocalImageUpload && (
        <MediaAttachmentUploadPreview
          mediaAttachment={attachment}
          size={mode === 'scroller' ? 'h-80 w-60' : mode === 'single' ? 'size-48 md:size-64' : 'size-48'}
          rounded="rounded-2xl"
          objectFit="cover"
        />
      )}

      {/* Markdown attachments */}
      {isMarkdownAttachment && (
        <MarkdownAttachment
          compact={!isScrollerMode}
          downloadUrl={isLocalMarkdownAttachment ? null : attachment.public_url}
          fileName={attachment.metadata?.file_name}
          size={isScrollerMode ? 'h-80 w-60' : 'h-[141px] w-30'}
          source={markdownSource}
          uploadError={attachment?.error}
          uploadProgress={attachment?.upload?.progress}
          uploadState={uploadState}
          renderMetadata={isLocalMarkdownAttachment ? undefined : renderMetadata}
        />
      )}

      {/* Video/Audio attachments */}
      {!shouldRenderUploadPreview &&
        (attachment.metadata?.content_type?.startsWith('video/') || attachment.metadata?.content_type?.startsWith('audio/')) && (
          <>
            {attachment.metadata?.content_type?.startsWith('video/') ? (
              <VideoAttachment attachment={attachment} mode={mode} />
            ) : (
              <div
                className={
                  isScrollerMode
                    ? 'flex h-80 w-80 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-neutral-100 p-4 dark:bg-neutral-900'
                    : 'min-h-14 w-full md:max-w-96'
                }
              >
                <MuxPlayer playbackId={attachment.variants?.mux?.playback_id} audio className="block w-full overflow-hidden" />
              </div>
            )}
          </>
        )}

      {/* PDF attachments */}
      {!shouldRenderUploadPreview && attachment.metadata?.content_type === 'application/pdf' && (
        <>
          {attachment.variants?.medium && (
            <>
              <PdfAttachment
                url={attachment.variants.medium.public_url}
                width={attachment.variants.medium.width}
                height={attachment.variants.medium.height}
                fileName={attachment.metadata.file_name}
                mode={mode}
                renderMetadata={renderMetadata}
                onOpen={canOpenImageLightbox ? handleOpenImageLightbox : undefined}
              />
            </>
          )}
        </>
      )}

      {!hasExternalImageLightbox && imageLightboxPayload && (
        <ImageAttachmentLightbox
          open={isImageLightboxOpen}
          onClose={handleCloseImageLightbox}
          src={imageLightboxPayload.src}
          publicUrl={imageLightboxPayload.publicUrl}
          previewSrc={imageLightboxPayload.previewSrc}
          srcset={imageLightboxPayload.srcset}
          alt={imageLightboxPayload.alt}
          width={imageLightboxPayload.width}
          height={imageLightboxPayload.height}
          thumbBounds={imageLightboxThumbBounds}
          slides={imageLightboxPayload.slides}
          index={imageLightboxPayload.index}
          channelId={channelId}
        />
      )}

      {/* Other file types */}
      {!shouldRenderUploadPreview &&
        !isImageAttachment &&
        !isMarkdownAttachment &&
        !attachment.metadata?.content_type?.startsWith('video/') &&
        !attachment.metadata?.content_type?.startsWith('audio/') &&
        attachment.metadata?.content_type !== 'application/pdf' && (
          <>
            {isScrollerMode ? (
              <a
                href={attachment.public_url}
                className="relative flex h-80 w-60 shrink-0 items-center justify-center rounded-2xl bg-neutral-100 p-4 dark:bg-neutral-900"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FilePlaceholderIcon />
                <Tooltip label="Download original" className="absolute right-2 bottom-2 left-2">
                  <span className="block truncate rounded-full bg-black/60 px-2 py-1 text-center font-mono text-3xs text-white shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.2)] backdrop-blur-[15px] backdrop-saturate-200">
                    {attachment.metadata?.file_name || 'Attachment'}
                  </span>
                </Tooltip>
              </a>
            ) : (
              <>
                <a
                  href={attachment.public_url}
                  className="flex size-30 items-center justify-center rounded-2xl bg-neutral-100 p-2 pb-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FilePlaceholderIcon />
                </a>
                <div className="flex justify-center pb-2">{renderMetadata()}</div>
              </>
            )}
          </>
        )}
    </div>
  );
};

export default Attachment;
